import { useState, useEffect, useRef } from 'react'
import { WORKSPACE_THEMES, type WorkspaceThemeId } from '../theme/workspaceThemes'
import { ensureReadingFonts } from '../utils/fontLoader'

function useScrollReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect() } },
      { threshold: 0.1 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return { ref, visible }
}

export function WorkspaceThemeShowcase() {
  const [activeId, setActiveId] = useState<WorkspaceThemeId>('warm_stone')
  const theme = WORKSPACE_THEMES.find(t => t.id === activeId)!
  const { ref: sectionRef, visible } = useScrollReveal<HTMLElement>()

  useEffect(() => {
    void ensureReadingFonts()
  }, [])

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden transition-colors duration-500"
      style={{ backgroundColor: 'var(--lp-bg)' }}
    >
      <div
        className="max-w-7xl mx-auto px-6 py-28 md:py-40 relative z-10 transition-all duration-700"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(32px)' }}
      >
        {/* Section header */}
        <div className="text-center mb-20 md:mb-24">
          <p
            className="text-[11px] font-semibold uppercase tracking-[0.3em] mb-4 transition-colors duration-500"
            style={{ color: 'var(--lp-text-3)' }}
          >
            工作区主题
          </p>
          <h2
            className="text-3xl md:text-5xl font-serif font-normal mb-5 leading-tight transition-colors duration-500"
            style={{ color: 'var(--lp-text)' }}
          >
            为每一种创作情绪
            <br />
            定制专属氛围
          </h2>
          <p
            className="max-w-lg mx-auto text-sm leading-relaxed transition-colors duration-500"
            style={{ color: 'var(--lp-text-2)' }}
          >
            六套精心调校的工作区主题，从极简黑白到热带暖调，让你的创作空间与灵感同频。
          </p>
        </div>

        {/* Content: info + preview */}
        <div className="grid lg:grid-cols-[320px_1fr] gap-10 lg:gap-14 items-start mb-14">
          {/* Left: theme info */}
          <div key={activeId} className="animate-theme-info flex flex-col gap-6 lg:sticky lg:top-8">
            {/* Theme name */}
            <div>
              <p
                className="text-[10px] font-semibold uppercase tracking-[0.25em] mb-2 transition-colors duration-500"
                style={{ color: 'var(--lp-text-3)' }}
              >
                当前主题
              </p>
              <h3
                className="text-3xl md:text-4xl font-serif font-normal leading-tight transition-colors duration-500"
                style={{ color: 'var(--lp-text)' }}
              >
                {theme.name}
              </h3>
            </div>

            {/* Keywords */}
            <div className="flex flex-wrap gap-2">
              {theme.keywords.map((kw) => (
                <span
                  key={kw}
                  className="px-3 py-1 rounded-full text-[11px] font-medium tracking-wide transition-colors duration-500 border"
                  style={{ borderColor: 'var(--lp-border)', color: 'var(--lp-text-2)' }}
                >
                  {kw}
                </span>
              ))}
            </div>

            {/* Color palette dots */}
            <div>
              <p
                className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 transition-colors duration-500"
                style={{ color: 'var(--lp-text-3)' }}
              >
                {theme.colorPalette}
              </p>
              <div className="flex items-center gap-2.5">
                {theme.swatches.map((color, i) => (
                  <div
                    key={i}
                    className="w-5 h-5 rounded-full transition-all duration-500 border"
                    style={{ backgroundColor: color, borderColor: 'var(--lp-border)' }}
                    title={color}
                  />
                ))}
              </div>
            </div>

            {/* Typography info */}
            <div
              className="flex flex-col gap-1.5 text-[11px] font-mono transition-colors duration-500"
              style={{ color: 'var(--lp-text-3)' }}
            >
              <span>Body: {theme.bodyFont} · Caption: {theme.captionFont}</span>
              <span>{theme.roundness} · {theme.elevation}</span>
            </div>
          </div>

          {/* Right: preview image with cross-fade */}
          <div className="relative">
            <div
              className="relative rounded-2xl overflow-hidden aspect-[16/10] transition-all duration-500"
              style={{
                backgroundColor: theme.cssVars['--ws-bg-app'],
                boxShadow: '0 25px 60px rgba(0,0,0,0.12), 0 0 0 1px var(--lp-border)',
              }}
            >
              {WORKSPACE_THEMES.map((t) => (
                <img
                  key={t.id}
                  src={t.previewImage}
                  alt={`${t.name} 主题预览`}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ease-in-out"
                  style={{ opacity: t.id === activeId ? 1 : 0 }}
                  loading="lazy"
                  decoding="async"
                />
              ))}
            </div>
            {/* Reflection glow under preview */}
            <div
              className="absolute -bottom-6 left-[15%] right-[15%] h-12 rounded-full blur-2xl opacity-10 transition-all duration-700"
              style={{ backgroundColor: theme.cssVars['--ws-accent'] }}
            />
          </div>
        </div>

        {/* Swatch card selector */}
        <div className="relative">
          {/* Mobile scroll fade */}
          <div
            className="absolute right-0 top-0 bottom-0 w-12 pointer-events-none z-10 lg:hidden transition-colors duration-500"
            style={{ background: `linear-gradient(to left, var(--lp-bg), transparent)` }}
          />

          <div className="flex gap-3 lg:gap-4 overflow-x-auto pb-4 snap-x snap-mandatory no-scrollbar lg:justify-center">
            {WORKSPACE_THEMES.map((t) => {
              const isActive = t.id === activeId
              const accentColor = t.cssVars['--ws-accent']

              return (
                <button
                  key={t.id}
                  onClick={() => setActiveId(t.id)}
                  className="group flex-shrink-0 snap-center flex flex-col items-stretch w-[100px] lg:w-[120px] rounded-xl overflow-hidden transition-all duration-300 ease-out cursor-pointer border focus:outline-none focus-visible:ring-2"
                  style={{
                    transform: isActive ? 'translateY(-8px) scale(1)' : undefined,
                    opacity: isActive ? 1 : 0.7,
                    borderColor: isActive ? 'var(--lp-border-hover)' : 'var(--lp-border)',
                    boxShadow: isActive
                      ? `0 8px 32px ${accentColor}25, 0 0 0 1px ${accentColor}15`
                      : undefined,
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.transform = 'translateY(-4px)'
                      e.currentTarget.style.opacity = '0.95'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.transform = ''
                      e.currentTarget.style.opacity = '0.7'
                    }
                  }}
                >
                  {/* Accent color block */}
                  <div
                    className="h-14 lg:h-16 transition-colors duration-500 relative"
                    style={{ backgroundColor: accentColor }}
                  >
                    {isActive && (
                      <div
                        className="absolute inset-0 animate-pulse opacity-25"
                        style={{
                          background: `radial-gradient(circle at center, ${accentColor}, transparent 70%)`,
                        }}
                      />
                    )}
                  </div>

                  {/* Theme name */}
                  <div
                    className="px-2 py-2.5 text-center transition-colors duration-500"
                    style={{ backgroundColor: 'var(--lp-surface)' }}
                  >
                    <span
                      className="text-[11px] font-semibold tracking-wide transition-colors duration-300"
                      style={{ color: isActive ? 'var(--lp-text)' : 'var(--lp-text-3)' }}
                    >
                      {t.name}
                    </span>
                  </div>

                  {/* Active indicator bar */}
                  <div
                    className="h-[2px] transition-all duration-300"
                    style={{
                      backgroundColor: isActive ? accentColor : 'transparent',
                      boxShadow: isActive ? `0 0 8px ${accentColor}50` : 'none',
                    }}
                  />
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
